import React, { useEffect, useState } from 'react';

import { Edit2, Trash2, Plus, Check, X } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import LoaderHelper from '../../utility/Loading/LoaderHelper';
import StockService from '../../Api_Module/Api_Services/StockService';
import { MarketSegment, TamAnalysisState, TamSummary } from '../../interface/ReasearchData';
import { setTamAnalysis, setTamSegment, updateTamSegment } from '../../store/ResearchDataSlice';

 

const TamAnalysis: React.FC = () => {
  const { symbol } = useParams<{ symbol?: string }>();
  const dispatch = useDispatch();
  const researchData = useSelector((state: any) => state.researchData);

  useEffect(() => {
    const fetchTamAnalysis = async () => {
      if (!symbol) return;
      const emptySegments = !Array.isArray(researchData?.tamAnalysis?.marketSegments) || researchData.tamAnalysis.marketSegments.length === 0;
      const summary = researchData?.tamAnalysis?.tamSummary || {};
      const emptySummary = !summary.TAM && !summary.SAM && !summary.SOM && !summary.Growth;
      if (emptySegments && emptySummary) {
        try {
          await getTamAnalysis();
        } catch (error) {
          console.error('Error fetching TAM analysis:', error);
        }
      }
    };
    fetchTamAnalysis();
  }, [symbol]);

  const getTamAnalysis = async () => {
    try {
      LoaderHelper.loaderStatus(true);
      const result = await StockService.getMarketSegmentDetails(symbol);
      if (Object.keys(result).length > 0) {
        const tamData: TamAnalysisState = {
          marketSegments: result?.market_segments ? result.market_segments.map((segment: any, index: number) => ({
            id: (index + 1).toString(),
            segment: segment.segment || '',
            marketSize: segment.market_size || '',
            growthRate: segment.growth_rate || '',
            addressability: segment.addressability || '',
            derivation: segment.derivation || ''
          })) : [],
          tamSummary: {
            TAM: result?.market_size_summary?.TAM || '',
            SAM: result?.market_size_summary?.SAM || '',
            SOM: result?.market_size_summary?.SOM || '',
            Growth: result?.market_size_summary?.Growth || '',
            methodology: ''
          }
        };
        dispatch(setTamAnalysis(tamData));
      }
      LoaderHelper.loaderStatus(false);
    } catch (error) {
      console.error('Error fetching TAM analysis:', error);
    }
  };

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<MarketSegment>({
    id: '',
    segment: '',
    marketSize: '',
    growthRate: '',
    addressability: '',
    derivation: '',
  });
  const [isAdding, setIsAdding] = useState(false);
  const [newSegment, setNewSegment] = useState<MarketSegment>({
    id: '',
    segment: '',
    marketSize: '',
    growthRate: '',
    addressability: '',
    derivation: '',
  });

  const [editingSummary, setEditingSummary] = useState(false);
  const [editSummary, setEditSummary] = useState<TamSummary>({
    TAM: '',
    SAM: '',
    SOM: '',
    Growth: '',
    methodology: '',
  });
  const handleEdit = (segment: MarketSegment) => {
    setEditingId(segment.id);
    setEditForm({
      ...segment
    });
  };
  const handleSaveEdit = () => {
    if (editForm.segment.trim() && editForm.marketSize.trim()) {
      dispatch(updateTamSegment({
        id: editingId || '',
        segment: editForm
      }));
      setEditingId(null);
      setEditForm({
        id: '',
        segment: '',
        marketSize: '',
        growthRate: '',
        addressability: '',
        derivation: '',
      });
    }
  };
  const handleCancelEdit = () => {
    setEditingId(null);
  };
  const handleDelete = (id: string) => {
    dispatch(setTamAnalysis({
      ...researchData.tamAnalysis,
      marketSegments: researchData.tamAnalysis.marketSegments.filter((segment: MarketSegment) => segment.id !== id)
    }));
  };
  const handleAddSegment = () => {
    if (newSegment.segment.trim() && newSegment.marketSize.trim()) {
      const newId = (Math.max(0, ...researchData.tamAnalysis.marketSegments.map((item: MarketSegment) => parseInt(item.id))) + 1).toString();
      dispatch(setTamSegment({
        ...newSegment,
        id: newId
      }));
      setNewSegment({
        id: '',
        segment: '',
        marketSize: '',
        growthRate: '',
        addressability: '',
        derivation: ''
      });
    }
    setIsAdding(false);
  };
  const handleSaveSummary = () => {
    if (editSummary.TAM.trim() && editSummary.SAM.trim() && editSummary.SOM.trim()) {
      dispatch(setTamAnalysis({
        ...researchData.tamAnalysis,
        tamSummary: editSummary
      }));
      setEditSummary({
        TAM: '',
        SAM: '',
        SOM: '',
        Growth: '',
        methodology: ''
      });
    }
    setEditingSummary(false);
  };
  return <div className="rounded-lg border border-theme-tertiary bg-theme-secondary shadow">
    {/* Market Summary */}
    <div className="bg-theme-tertiary border border-theme-tertiary rounded-lg p-4 m-3 mb-4">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-sm font-semibold text-theme-primary">
          Market Size Summary
        </h3>
        {!editingSummary && <button onClick={() => {
          setEditSummary({
            ...researchData?.tamAnalysis?.tamSummary
          });
          setEditingSummary(true);
        }} className="p-1 text-theme-muted hover:text-theme-primary" aria-label="Edit summary">
          <Edit2 size={14} />
        </button>}
      </div>
      {editingSummary ? <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
        <div>
          <label className="block text-xs font-medium text-theme-primary mb-1">
            Total Addressable Market (TAM)
          </label>
          <input type="text" value={editSummary?.TAM} onChange={e => setEditSummary({
            ...editSummary,
            TAM: e.target.value
          })} className="w-full p-2 border border-theme-tertiary rounded bg-theme-secondary text-theme-primary text-sm" />
        </div>
        <div>
          <label className="block text-xs font-medium text-theme-primary mb-1">
            Serviceable Available Market (SAM)
          </label>
          <input type="text" value={editSummary?.SAM} onChange={e => setEditSummary({
            ...editSummary,
            SAM: e.target.value
          })} className="w-full p-2 border border-theme-tertiary rounded bg-theme-secondary text-theme-primary text-sm" />
        </div>
        <div>
          <label className="block text-xs font-medium text-theme-primary mb-1">
            Serviceable Obtainable Market (SOM)
          </label>
          <input type="text" value={editSummary?.SOM} onChange={e => setEditSummary({
            ...editSummary,
            SOM: e.target.value
          })} className="w-full p-2 border border-theme-tertiary rounded bg-theme-secondary text-theme-primary text-sm" />
        </div>
        <div>
          <label className="block text-xs font-medium text-theme-primary mb-1">
            Projected Annual Growth
          </label>
          <input type="text" value={editSummary?.Growth} onChange={e => setEditSummary({
            ...editSummary,
            Growth: e.target.value
          })} className="w-full p-2 border border-theme-tertiary rounded bg-theme-secondary text-theme-primary text-sm" />
        </div>
        <div className="md:col-span-2 flex gap-2 justify-end">
          <button onClick={handleSaveSummary} className="px-3 py-1 text-xs btn btn-sm active text-theme-primary rounded hover:bg-blue-700">
            Save
          </button>
          <button onClick={() => setEditingSummary(false)} className="px-3 py-1 text-xs btn btn-sm text-theme-primary rounded hover:bg-theme-tertiary/70 border border-theme-tertiary">
            Cancel
          </button>
        </div>
      </div> : <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-theme-secondary p-3 rounded-lg shadow border border-theme-tertiary">
          <div className="text-xs text-theme-muted">TAM</div>
          <div className="text-2xl font-semibold text-theme-primary">
            {researchData?.tamAnalysis?.tamSummary?.TAM || 0}
          </div>
        </div>
        <div className="bg-theme-secondary p-3 rounded-lg shadow border border-theme-tertiary">
          <div className="text-xs text-theme-muted">SAM</div>
          <div className="text-2xl font-semibold text-theme-primary">
            {researchData?.tamAnalysis?.tamSummary?.SAM || 0}
          </div>
        </div>
        <div className="bg-theme-secondary p-3 rounded-lg shadow border border-theme-tertiary">
          <div className="text-xs text-theme-muted">SOM</div>
          <div className="text-2xl font-semibold text-theme-primary">
            {researchData?.tamAnalysis?.tamSummary?.SOM || 0}
          </div>
        </div>
        <div className="bg-theme-secondary p-3 rounded-lg shadow border border-theme-tertiary">
          <div className="text-xs text-theme-muted">Growth</div>
          <div className="text-2xl font-semibold text-theme-primary">
            {researchData?.tamAnalysis?.tamSummary?.Growth || 0}
          </div>
        </div>
      </div>}
    </div>

    <div className="bg-theme-secondary rounded-lg overflow-hidden border border-theme-tertiary m-3">
      <table className="w-full text-left">
        <thead>
          <tr className="bg-theme-tertiary">
            <th className="px-3 py-2 text-left text-theme-primary text-xs">
              Market Segment
            </th>
            <th className="px-3 py-2 text-left text-theme-primary text-xs">
              Market Size
            </th>
            <th className="px-3 py-2 text-left text-theme-primary text-xs">
              Growth Rate
            </th>
            <th className="px-3 py-2 text-left text-theme-primary text-xs">
              Addressability
            </th>
            <th className="px-3 py-2 text-right text-theme-primary text-xs">
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          {researchData?.tamAnalysis?.marketSegments?.map((segment: any) => <tr key={segment.id} className="border-t border-theme-tertiary">
            {editingId === segment.id ? <>
              <td className="px-3 py-2">
                <input type="text" value={editForm.segment} onChange={e => setEditForm({
                  ...editForm,
                  segment: e.target.value
                })} className="w-full p-1 border border-theme-tertiary rounded bg-theme-secondary text-theme-primary" />
              </td>
              <td className="px-3 py-2">
                <input type="text" value={editForm.marketSize} onChange={e => setEditForm({
                  ...editForm,
                  marketSize: e.target.value
                })} className="w-full p-1 border border-theme-tertiary rounded bg-theme-secondary text-theme-primary" />
              </td>
              <td className="px-3 py-2">
                <input type="text" value={editForm.growthRate} onChange={e => setEditForm({
                  ...editForm,
                  growthRate: e.target.value
                })} className="w-full p-1 border border-theme-tertiary rounded bg-theme-secondary text-theme-primary" />
              </td>
              <td className="px-3 py-2">
                <select value={editForm.addressability} onChange={e => setEditForm({
                  ...editForm,
                  addressability: e.target.value
                })} className="w-full p-1 border border-theme-tertiary rounded bg-theme-secondary text-theme-primary">
                  <option value="Low">Low</option>
                  <option value="Medium-Low">Medium-Low</option>
                  <option value="Medium">Medium</option>
                  <option value="Medium-High">Medium-High</option>
                  <option value="High">High</option>
                </select>
              </td>
              <td className="px-3 py-2 text-right">
                <div className="flex justify-end gap-2">
                  <button onClick={handleSaveEdit} className="p-1 bg-green-600 text-theme-primary rounded hover:bg-green-700" aria-label="Save edit">
                    <Check size={16} />
                  </button>
                  <button onClick={handleCancelEdit} className="p-1 bg-theme-tertiary text-theme-primary rounded hover:bg-theme-tertiary/70 border border-theme-tertiary" aria-label="Cancel edit">
                    <X size={16} />
                  </button>
                </div>
              </td>
            </> : <>
              <td className="px-3 py-2 text-theme-primary">
                {segment.segment}
              </td>
              <td className="px-3 py-2 text-theme-primary">
                {segment.marketSize}
              </td>
              <td className="px-3 py-2 text-theme-primary">
                {segment.growthRate}
              </td>
              <td className="px-3 py-2 text-theme-primary">
                {segment.addressability}
              </td>
              <td className="px-3 py-2 text-right">
                <div className="flex justify-end gap-2">
                  <button onClick={() => handleEdit(segment)} className="p-1 text-theme-muted hover:text-blue-600" aria-label="Edit segment">
                    <Edit2 size={16} />
                  </button>
                  <button onClick={() => handleDelete(segment.id)} className="p-1 text-theme-muted hover:text-red-600" aria-label="Delete segment">
                    <Trash2 size={16} />
                  </button>
                </div>
              </td>
            </>}
          </tr>)}
          {isAdding && <tr className="border-t border-theme-tertiary">
            <td className="px-3 py-2">
              <input type="text" value={newSegment.segment} onChange={e => setNewSegment({
                ...newSegment,
                segment: e.target.value
              })} className="w-full p-1 border border-theme-tertiary rounded bg-theme-secondary text-theme-primary" placeholder="Market segment" />
            </td>
            <td className="px-3 py-2">
              <input type="text" value={newSegment.marketSize} onChange={e => setNewSegment({
                ...newSegment,
                marketSize: e.target.value
              })} className="w-full p-1 border border-theme-tertiary rounded bg-theme-secondary text-theme-primary" placeholder="Market size" />
            </td>
            <td className="px-3 py-2">
              <input type="text" value={newSegment.growthRate} onChange={e => setNewSegment({
                ...newSegment,
                growthRate: e.target.value
              })} className="w-full p-1 border border-theme-tertiary rounded bg-theme-secondary text-theme-primary" placeholder="Growth rate" />
            </td>
            <td className="px-3 py-2">
              <select value={newSegment.addressability} onChange={e => setNewSegment({
                ...newSegment,
                addressability: e.target.value
              })} className="w-full p-1 border border-theme-tertiary rounded bg-theme-secondary text-theme-primary">
                <option value="">Select addressability</option>
                <option value="Low">Low</option>
                <option value="Medium-Low">Medium-Low</option>
                <option value="Medium">Medium</option>
                <option value="Medium-High">Medium-High</option>
                <option value="High">High</option>
              </select>
            </td>
            <td className="px-3 py-2 text-right">
              <div className="flex justify-end gap-2">
                <button onClick={handleAddSegment} className="p-1 btn btn-sm active bg-green-600 text-theme-primary rounded hover:bg-green-700" aria-label="Save new segment">
                  <Check size={16} />
                </button>
                <button onClick={() => setIsAdding(false)} className="p-1 btn btn-sm text-theme-primary rounded hover:bg-theme-tertiary/70 border border-theme-tertiary" aria-label="Cancel adding segment">
                  <X size={16} />
                </button>
              </div>
            </td>
          </tr>}
        </tbody>
      </table>
      {!isAdding && <div className="p-3 border-t border-theme-tertiary">
        <button onClick={() => setIsAdding(true)} className="flex items-center gap-2 px-3 py-1.5 text-xs btn btn-sm active rounded hover:bg-theme-tertiary/70 border border-theme-tertiary">
          <Plus size={14} />
          Add Market Segment
        </button>
      </div>}
    </div>
  </div>;
}
export default TamAnalysis;